<script setup lang="ts">
import {computed, ref} from "vue";
import axios from "axios";
import {route} from "ziggy-js";

const value = defineModel<boolean>()

const submit = () => {
    axios.post(route('onboarding.completed'), {
        onboarding_completed: true,
    }).then(() => {
        value.value = false
    })
}

const step = ref<number>(1)

const steps: Record<number,{ title: string; description: string; }> = {
    1: {
        title: 'Welcome',
        description: 'This portal is designed to help you assess and enhance your resource\'s capacity in various domains.'
    },
    2: {
        title: 'Resources',
        description: 'Create or join resources to start assessing your capacity and collaborate with your colleagues.'
    },
    3: {
        title: 'Assessments',
        description: 'Use assessments to evaluate your resource\'s capacity and identify areas for improvement.'
    },
    4: {
        title: 'Frameworks',
        description: 'Explore and contribute to capacity framework templates to help build a stronger community.'
    },
    5: {
        title: 'Documentation',
        description: 'Access comprehensive documentation to guide you through using the portal effectively.'
    },
    6: {
        title: 'Videos & Tutorials',
        description: 'Watch videos and tutorials to learn how to make the most of the portal'
    }
}

const nextStep = () => {
    if (step.value < 6) {
        step.value += 1
    } else {
        submit()
    }
}

const previousStep = () => {
    if (step.value > 1) {
        step.value -= 1
    }
}

const isBackDisabled = computed(() => {
    return step.value === 1
})

const isLastStep = computed(() => {
    return step.value === 6
})
</script>

<template>
    <v-dialog
        v-model="value"
        max-width="600"
        scrim="blue-grey-darken-4"
        opacity="0.5"
        transition="dialog-bottom-transition"
    >
        <v-card
            v-click-outside="submit"
            flat
            width="600"
            color="white"
            rounded="lg"
        >
            <v-slide-x-transition hide-on-leave>
                <img
                    v-show="step === 1"
                    src="@/assets/welcome.png"
                    alt="Welcome"
                >
            </v-slide-x-transition>
            <v-slide-x-transition hide-on-leave>
                <img
                    v-show="step === 2"
                    src="@/assets/teams.png"
                    alt="Resources"
                >
            </v-slide-x-transition>
            <v-slide-x-transition hide-on-leave>
                <img
                    v-show="step === 3"
                    src="@/assets/assessments.png"
                    alt="Assessments"
                >
            </v-slide-x-transition>
            <v-slide-x-transition hide-on-leave>
                <img
                    v-show="step === 4"
                    src="@/assets/templates.png"
                    alt="Capacity Framework Templates"
                >
            </v-slide-x-transition>
            <v-slide-x-transition hide-on-leave>
                <img
                    v-show="step === 5"
                    src="@/assets/documentation.png"
                    alt="Documentation"
                >
            </v-slide-x-transition>
            <v-slide-x-transition hide-on-leave>
                <img
                    v-show="step === 6"
                    src="@/assets/tutorials.png"
                    alt="Videos & Tutorials"
                >
            </v-slide-x-transition>
            <v-card-text class="text-center">
                <p class="text-h5 font-weight-bold">
                    {{ steps[step].title }}
                </p>
                <p class="text-medium-emphasis my-5 justify-center">
                    {{ steps[step].description }}
                </p>
            </v-card-text>
            <v-card-actions class="justify-center">
                <v-btn
                    variant="text"
                    color="base"
                    class="border-sm px-15"
                    size="large"
                    :disabled="isBackDisabled"
                    @click="previousStep"
                >
                    Back
                </v-btn>
                <v-btn
                    variant="flat"
                    class="px-15"
                    size="large"
                    @click="nextStep"
                >
                    {{ isLastStep ? 'Done' :'Next' }}
                </v-btn>
            </v-card-actions>
            <v-card-actions class="justify-center pb-5">
                <v-rating
                    v-model="step"
                    :length="6"
                    color="primary"
                    empty-icon="mdi-circle-outline"
                    full-icon="mdi-checkbox-blank-circle"
                    readonly
                    density="compact"
                >
                    <template #item="{ index, props }">
                        <v-icon
                            v-bind="props"
                            size="12"
                            :color="index + 1 <= step ? 'primary' : 'grey-lighten-4'"
                        >
                            mdi-checkbox-blank-circle
                        </v-icon>
                    </template>
                </v-rating>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>

</style>
